import type { Priority, Size, StatusCategory } from "@beacon/core/projects"
import { BookmarkIcon, BugIcon, CircleSlashIcon, ZapIcon } from "lucide-react"
import { useId, type ReactNode } from "react"
import { Link } from "react-router"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/primitives/tooltip.tsx"
import { cn } from "@/primitives/utils"
import type { Identity } from "./identity.ts"
import { HueDot } from "./identity.tsx"
import { pieFractionOf, piePath, type StatusStep } from "./ticket-glyphs.ts"

/*
 * A ticket's facts as glyphs, so a row can be read at a glance: its Status's circle, its priority's
 * bars, its type, and the chips for its Size, for being blocked, in an Epic or in an Area, for the
 * Labels it carries, and for the chips a row had no room for. Every glyph is 14px, drawn in
 * `currentColor`, and hidden from a screen reader; where one stands alone, with nothing beside it
 * saying the same in words, it names itself in a tooltip and to a screen reader instead.
 */

/**
 * Names a glyph that stands alone: a tooltip for the eye and a label for a screen reader. One that
 * sits beside its own words passes no `label`, and is left as decoration.
 */
function Labelled({ label, children }: { label: string | undefined; children: ReactNode }) {
  if (!label) return children
  return (
    <Tooltip>
      <TooltipTrigger
        render={<span role="img" className="inline-flex shrink-0" />}
        aria-label={label}
      >
        {children}
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  )
}

const statusColours: Record<StatusCategory, string> = {
  "not-started": "text-status-unstarted",
  started: "text-status-started",
  done: "text-status-done",
  cancelled: "text-status-cancelled",
}

/**
 * A Status's circle, by its category, never its name: an outline for Not started; an outline with a
 * pie for Started, filling by `step` (from `statusGlyphOf`) as the ticket moves right; a filled
 * circle with a check for Done, and with a cross for Cancelled. The check and cross are cut out of
 * the disc with a mask, so the surface shows through on any row, card or hover fill.
 *
 * `name` is the Status's name, for a circle that stands alone.
 */
export function StatusIcon({
  category,
  step,
  name,
}: {
  category: StatusCategory
  step?: StatusStep
  name?: string
}) {
  const mask = useId()
  return (
    <Labelled label={name}>
      <svg
        viewBox="0 0 14 14"
        aria-hidden
        className={cn("size-3.5 shrink-0", statusColours[category])}
      >
        {category === "not-started" && (
          <circle cx="7" cy="7" r="5.25" fill="none" stroke="currentColor" strokeWidth="1.5" />
        )}
        {category === "started" && (
          <>
            <circle cx="7" cy="7" r="5.25" fill="none" stroke="currentColor" strokeWidth="1.5" />
            <path d={piePath(pieFractionOf(step), 7, 7, 3)} fill="currentColor" />
          </>
        )}
        {(category === "done" || category === "cancelled") && (
          <>
            <mask id={mask}>
              <rect width="14" height="14" fill="white" />
              <path
                d={category === "done" ? "M4.5 7.2 6.2 8.9 9.6 5.4" : "M5 5l4 4M9 5l-4 4"}
                fill="none"
                stroke="black"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </mask>
            <circle cx="7" cy="7" r="6" fill="currentColor" mask={`url(#${mask})`} />
          </>
        )}
      </svg>
    </Labelled>
  )
}

const priorityWords: Record<Priority, string> = { high: "High", medium: "Medium", low: "Low" }
const barsFilled: Record<Priority, number> = { high: 3, medium: 2, low: 1 }

/** The three bars, 2.5px wide and 5, 8 and 11px tall, standing on one baseline 1.5px apart. */
const bars = [
  { x: 1.75, height: 5 },
  { x: 5.75, height: 8 },
  { x: 9.75, height: 11 },
] as const

/**
 * A ticket's priority as rising bars: High fills all three, Medium two, Low one. `standalone`
 * names it in a tooltip, "Priority: High", where no word sits beside it.
 */
export function PriorityIcon({
  priority,
  standalone,
}: {
  priority: Priority
  standalone?: boolean
}) {
  return (
    <Labelled label={standalone ? `Priority: ${priorityWords[priority]}` : undefined}>
      <svg viewBox="0 0 14 14" aria-hidden className="size-3.5 shrink-0">
        {bars.map(({ x, height }, index) => (
          <rect
            key={x}
            x={x}
            y={12.5 - height}
            width="2.5"
            height={height}
            rx="0.5"
            className={index < barsFilled[priority] ? "fill-foreground" : "fill-foreground/25"}
          />
        ))}
      </svg>
    </Labelled>
  )
}

/**
 * Each Size's square, in px of the frame's 11.5: XS a dot, XL most of it, always with a margin, so a
 * large Size never reads as a solid block beside the priority's bars.
 */
const sizeSide: Record<Size, number> = { xs: 2.5, s: 4, m: 5.5, l: 7, xl: 8.5 }

/**
 * A ticket's Size as a square growing inside a frame, smallest first; an unsized ticket's frame is
 * dashed and empty. It sits where a property's glyph does, beside the Size in words.
 */
export function SizeIcon({ size }: { size: Size | null }) {
  const side = size ? sizeSide[size] : 0
  return (
    <svg viewBox="0 0 14 14" aria-hidden className="size-3.5 shrink-0">
      <rect
        x="1.25"
        y="1.25"
        width="11.5"
        height="11.5"
        rx="2.5"
        fill="none"
        strokeWidth="1.5"
        strokeDasharray={size ? undefined : "2 2"}
        className={size ? "stroke-foreground/25" : "stroke-subtle-foreground"}
      />
      {size && (
        <rect
          x={7 - side / 2}
          y={7 - side / 2}
          width={side}
          height={side}
          rx={Math.min(1.5, side / 3)}
          className="fill-foreground"
        />
      )}
    </svg>
  )
}

/** What a ticket is. An Epic is a ticket of its own kind here, though core types it apart. */
export type TicketKind = "story" | "bug" | "epic"

const types = {
  story: { Icon: BookmarkIcon, colour: "text-muted-foreground", word: "Story" },
  bug: { Icon: BugIcon, colour: "text-ticket-bug", word: "Bug" },
  epic: { Icon: ZapIcon, colour: "text-ticket-epic", word: "Epic" },
} as const

/**
 * A ticket's type. Rows and cards leave a Story's out, as the common case, and always show a Bug's
 * and an Epic's; a ticket's own properties show all three.
 */
export function TypeIcon({ type, standalone }: { type: TicketKind; standalone?: boolean }) {
  const { Icon, colour, word } = types[type]
  return (
    <Labelled label={standalone ? word : undefined}>
      <Icon aria-hidden className={cn("size-3.5 shrink-0", colour)} />
    </Labelled>
  )
}

const chip = "inline-flex h-5 shrink-0 items-center gap-1 rounded-md px-1.5 text-2xs font-medium"

/**
 * Where a chip sits, when not in a row of its own: `card` is a Board card's one row of chips, which
 * never wraps. There the Epic's and the Area's chips give up width, down to a few letters, to share
 * what the row has left, and a Label's keeps its width, cut short past 6rem.
 */
type ChipPlace = "card"

/**
 * A ticket's Size, abbreviated: XS, S, M, L or XL, each the same width so a column of them lines
 * up. `name` is the Size in full, which a chip standing alone names in its tooltip; a chip beside
 * its own words, as in a picker, passes none.
 */
export function SizeChip({ size, name }: { size: Size; name?: string }) {
  return (
    <Labelled label={name && `Size: ${name}`}>
      <span className={cn(chip, "min-w-6 justify-center bg-muted text-muted-foreground")}>
        {size.toUpperCase()}
      </span>
    </Labelled>
  )
}

/**
 * Says a ticket can't move until others are done. Its tooltip names them, "Blocked by VRF-2,
 * OTH-1", when `blockers` holds their keys.
 */
export function BlockedChip({ blockers }: { blockers?: readonly string[] }) {
  const body = (
    <span className={cn(chip, "bg-blocked/10 text-blocked")}>
      <CircleSlashIcon aria-hidden className="size-3" />
      Blocked
    </span>
  )
  if (!blockers?.length) return body
  return (
    <Tooltip>
      <TooltipTrigger render={body} />
      <TooltipContent>Blocked by {blockers.join(", ")}</TooltipContent>
    </Tooltip>
  )
}

/**
 * The Area a ticket is in, as its hue's dot and its name, truncated to fit with the whole name in
 * the tooltip. The hue tells Areas apart and says nothing about the work.
 */
export function AreaChip({ name, hue, on }: { name: string; hue: Identity; on?: ChipPlace }) {
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <span
            className={cn(chip, "max-w-32 bg-muted text-muted-foreground", on && "min-w-16 shrink")}
          />
        }
      >
        <HueDot hue={hue} />
        <span className="truncate">{name}</span>
      </TooltipTrigger>
      <TooltipContent>{name}</TooltipContent>
    </Tooltip>
  )
}

// An outlined pill, where an Area's chip is a filled one: a ticket is in one Area and carries any
// number of Labels.
const labelChip =
  "inline-flex h-5 max-w-full min-w-0 items-center gap-1 rounded-full px-1.5 text-2xs font-medium text-muted-foreground ring-1 ring-border ring-inset"

/**
 * One Label, as its hue's dot and its name; a name too long for where it sits is cut short, whole
 * in its tooltip. The hue tells Labels apart and says nothing about the work.
 */
export function LabelChip({ name, hue, on }: { name: string; hue: Identity; on?: ChipPlace }) {
  return (
    <Tooltip>
      <TooltipTrigger render={<span className={cn(labelChip, on && "max-w-24 shrink-0")} />}>
        <HueDot hue={hue} />
        <span className="truncate">{name}</span>
      </TooltipTrigger>
      <TooltipContent>{name}</TooltipContent>
    </Tooltip>
  )
}

/**
 * The chips a row had no room for, counted as "+N", with their names in its tooltip, so a row of
 * chips stays one line however many a ticket has.
 */
export function MoreChip({ names }: { names: readonly string[] }) {
  return (
    <Tooltip>
      <TooltipTrigger render={<span className={cn(labelChip, "shrink-0 tabular-nums")} />}>
        +{names.length}
      </TooltipTrigger>
      <TooltipContent>{names.join(", ")}</TooltipContent>
    </Tooltip>
  )
}

/**
 * The Epic a ticket is in, by its title truncated to fit, or its key while the title is unknown. The
 * tooltip gives both in full. With `href` it is a link to the Epic.
 */
export function EpicChip({
  epicKey,
  title,
  href,
  on,
}: {
  epicKey: string
  title?: string
  href?: string
  on?: ChipPlace
}) {
  const className = cn(
    chip,
    "max-w-40 bg-muted text-muted-foreground",
    on && "min-w-16 shrink",
    href && "transition-colors duration-100 ease-snappy hover:text-foreground",
  )
  const content = (
    <>
      <ZapIcon aria-hidden className="size-3 shrink-0 text-ticket-epic" />
      <span className="truncate">{title ?? epicKey}</span>
    </>
  )
  return (
    <Tooltip>
      <TooltipTrigger
        render={href ? <Link to={href} className={className} /> : <span className={className} />}
      >
        {content}
      </TooltipTrigger>
      <TooltipContent>{title ? `${epicKey} ${title}` : epicKey}</TooltipContent>
    </Tooltip>
  )
}
